import { Log } from '@agilelab/plugin-wb-platform';
import {
  getCompoundEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { JsonObject } from '@backstage/types';
import { PropTypes } from '@material-ui/core';
import { FormProps } from '@rjsf/core';
import { ErrorSchema } from '@rjsf/utils';
import { WmUiSchema } from '../../extensions/types';
import {
  deserializeEnvironmentParamKey,
  isEnvironmentParamKey,
} from '@agilelab/plugin-wb-platform-common';

export type Step = {
  schema: JsonObject;
  title: string;
  asyncValidate?: (formData: any) => Promise<ErrorSchema>;
} & Partial<Omit<FormProps<any>, 'schema'>>;

export type AdditionalStep = {
  title: string;
  component: JSX.Element;
  additionalActions?: JSX.Element[];
  backButtonText?: string;
  nextButtonText?: string;
  disableStepButtons: boolean;
  customDisableStepButtons?: {
    disableNext?: boolean;
    disableReset?: boolean;
    disableBack?: boolean;
  };
  analyticsEvent?: string;
  backAction?: () => void;
  nextAction?: () => Promise<void>;
  resetButtonText?: string;
  resetAction?: () => void;
  resetButtonVariant?: 'text' | 'outlined' | 'contained';
  resetButtonColor?: PropTypes.Color;
  taskFailed?: boolean;
  onSaveButtonClick?: (params: {
    setStep: (step: number) => void;
    setAdditionalStep: (step: number) => void;
  }) => void;
};

export type Logs =
  | {
      type: 'text-logs';
      text: string;
    }
  | {
      type: 'structured-logs';
      logs: Log[];
    };

function parseEntityRef(entityLike: any): string {
  try {
    return stringifyEntityRef(getCompoundEntityRef(entityLike));
  } catch (error) {
    return '<no name>';
  }
}

export function getReviewData(formData: Record<string, any>, steps: Step[]) {
  const uiSchemas = getUiSchemasFromSteps(steps);

  const reviewData: Record<string, any> = {};

  uiSchemas.forEach(uiSchema => {
    const key = uiSchema.name;
    const value = formData[key];

    if (Array.isArray(value) && value.length === 0) return;

    const processedValue = processValueByUiSchema(value, uiSchema);

    if (processedValue === null || processedValue === undefined) return;

    pushToReviewData(reviewData, key, processedValue, uiSchema);
  });

  return reviewData;
}

function processValueByUiSchema(value: any, uiSchema?: any): any {
  if (!uiSchema) return value;

  if (uiSchema['ui:widget'] === 'password') {
    return '******';
  }

  if (uiSchema['ui:field']?.toString().toLowerCase() === 'entitypicker') {
    return value && typeof value === 'string'
      ? value
      : `<descriptor> ${parseEntityRef(value)}`;
  }

  if (uiSchema['ui:field']?.toString().toLowerCase() === 'filepicker') {
    return `File: ${value?.fileName ?? 'File'}`;
  }

  if (uiSchema['ui:backstage']?.review) {
    const review = uiSchema['ui:backstage'].review as JsonObject;

    if (review.mask) {
      return review.mask;
    }

    if (!review.show) {
      return null;
    }
  }

  return value;
}

function pushToReviewData(
  reviewData: Record<string, any>,
  key: string,
  processedValue: any,
  uiSchema?: any,
) {
  const groupKey = uiSchema?.env;
  const displayKey = uiSchema?.title ?? key;

  const deserializedValue = deserializeEnvironmentKeysDeep(processedValue);

  if (groupKey) {
    if (!reviewData[groupKey]) {
      reviewData[groupKey] = {};
    }
    reviewData[groupKey][displayKey] = deserializedValue;
  } else {
    reviewData[displayKey] = deserializedValue;
  }
}

function deserializeEnvironmentKeysDeep(value: any): any {
  if (Array.isArray(value)) {
    return value.map(item => deserializeEnvironmentKeysDeep(item));
  }

  if (typeof value === 'object' && value !== null) {
    const result: Record<string, any> = {};

    for (const [key, val] of Object.entries(value)) {
      let newKey = key;
      if (isEnvironmentParamKey(key)) {
        const { key: deserialized } = deserializeEnvironmentParamKey(key);
        newKey = deserialized;
      }

      result[newKey] = deserializeEnvironmentKeysDeep(val);
    }

    return result;
  }

  return value;
}

export function getUiSchemasFromSteps(steps: Step[]): any[] {
  const uiSchemas: Array<any> = [];
  steps.forEach(step => {
    const schemaProps = step.schema.properties as JsonObject;
    const areEnvironmentParameters = step.schema.environmentParameters;

    for (const key in schemaProps) {
      if (schemaProps.hasOwnProperty(key)) {
        const originalUiSchema =
          schemaProps[key] && typeof schemaProps[key] === 'object'
            ? schemaProps[key]
            : {};

        const uiSchema: WmUiSchema = {
          ...originalUiSchema,
          name: key,
          ...(areEnvironmentParameters && {
            env: deserializeEnvironmentParamKey(key).env,
          }),
        };

        uiSchemas.push(uiSchema);
      }
    }
  });

  return uiSchemas;
}
